import { Group, NumberInput, TextInput, Tooltip } from '@mantine/core';
import { IconArrowRight, IconCopy, IconTrash } from '@tabler/icons-react';
import { isEvidenceFieldDeviceProperty } from 'components/forms/remote-source/config/device-properties';
import { ChastiaConfigForm, ISObjectPairing } from 'components/forms/remote-source/config/types';
import DevicePropertySelect from 'components/selects/DevicePropertySelect';
import { DepartmentSelect } from 'components/selects/DepartmentSelect';
import { EvidenceFieldSelect } from 'components/selects/EvidenceFieldSelect';
import { useCallback } from 'react';
import { useConfirm } from 'components/modals/message/MessageProvider';
import ActionIcon from 'components/ActionIcon';

interface ISObjectPairingRowProps {
  form: ChastiaConfigForm;
  item: ISObjectPairing;
  index: number;
}

/**
 * A single row for IS Object Pairing configuration.
 */
export default function ISObjectPairingRow({ form, item, index }: ISObjectPairingRowProps) {
  const { confirm } = useConfirm();

  const cloneRow = useCallback(() => {
    form.insertListItem('objects', { ...item }, index);
  }, [form, item, index]);

  const removeRow = useCallback(() => {
    if (!item.structureId && !item.levelId && !item.isObjectId && !item.property && !item.value) {
      form.removeListItem('objects', index);
    } else {
      confirm({
        title: 'Odstrániť riadok?',
        content: 'Naozaj chcete odstrániť tento riadok mapovania IS objektov?',
        onConfirm: () => form.removeListItem('objects', index),
      });
    }
  }, [confirm, form, item, index]);

  return (
    <Group key={item.key} className="grid grid-cols-[1fr_1fr_1fr_24px_1fr_1fr_88px] items-center gap-y-0">
      <NumberInput size="md" {...form.getInputProps(`objects.${index}.structureId`)} />
      <NumberInput size="md" {...form.getInputProps(`objects.${index}.levelId`)} />
      <NumberInput size="md" placeholder="Akýkoľvek IS Objekt" {...form.getInputProps(`objects.${index}.isObjectId`)} />
      <IconArrowRight stroke={1.5} />
      <DevicePropertySelect {...form.getInputProps(`objects.${index}.property`)} />
      {item.property === 'department' ? (
        <DepartmentSelect groupByOrganization size="md" {...form.getInputProps(`objects.${index}.value`)} />
      ) : isEvidenceFieldDeviceProperty(item.property) ? (
        <EvidenceFieldSelect applicability="devices" size="md" {...form.getInputProps(`objects.${index}.value`)} />
      ) : (
        <TextInput size="md" disabled readOnly value="Názov IS Objektu" />
      )}
      <Group spacing={8} noWrap>
        <Tooltip label="Duplikovať riadok" openDelay={500} withArrow={false}>
          <ActionIcon variant="subtle-gray" size="md" onClick={cloneRow}>
            <IconCopy />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Odstrániť riadok" openDelay={500} withArrow={false}>
          <ActionIcon variant="subtle" size="md" onClick={removeRow}>
            <IconTrash />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  );
}
